// src/app/services/silencerApi.ts
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from './apiConfig';
import {
  ISilencer,
  ISilencerParams,
  ISilencerSizes,
  UpdatePayload,
} from '../models';

export const silencerApi = createApi({
  reducerPath: 'silencerApi',
  baseQuery,
  endpoints: (builder) => ({
    getSilencerParams: builder.query<{ silencer: ISilencerParams }, void>({
      query: () => `/api/v1.0/cms/1/v1.0.0/ahu/silencers/params/`,
      keepUnusedDataFor: Infinity, // Keep the cached data until the page reloads
    }),
    getSilencer: builder.query<ISilencer, number>({
      query: (id) => `/api/v1.0/ahu/silencers/${id}`,
      transformResponse: (data: ISilencer) => {
        data.silencerParams = data.silencer_params
          ? JSON.parse(data.silencer_params)
          : {};
        return data;
      },
    }),
    getAllSilencersCount: builder.query<{ count: number }, void>({
      query: () => `/api/v1.0/cms/1/v1.0.0/ahu/silencers/all/count/`,
      keepUnusedDataFor: Infinity, // Keep the cached data until the page reloads
    }),
    getSilencersCount: builder.query<{ count: number }, string>({
      query: (queryString) =>
        `/api/v1.0/cms/1/v1.0.0/ahu/silencers/all/count/?${queryString}`,
      extraOptions: {
        cacheData: true,
      },
    }),
    getSilencersWidths: builder.query<
      number[],
      { mfg_id: number; series_id: number }
    >({
      query: ({ mfg_id, series_id }) =>
        `/api/v1.0/master/v1.0.0/ahu/silencers/widths/?mfg_id=${mfg_id}&series_id=${series_id}`,
      extraOptions: {
        cacheData: true,
      },
      transformResponse: (data: ISilencerSizes[]): number[] => {
        var res = [];
        for (let model of data) {
          for (let i = model.range_min; i <= model.range_max; i++) {
            res.push(i);
          }
        }
        return res;
      },
    }),
    getSilencersHeights: builder.query<
      number[],
      { mfg_id: number; series_id: number }
    >({
      query: ({ mfg_id, series_id }) =>
        `/api/v1.0/master/v1.0.0/ahu/silencers/heights/?mfg_id=${mfg_id}&series_id=${series_id}`,
      extraOptions: {
        cacheData: true,
      },
      transformResponse: (data: ISilencerSizes[]): number[] => {
        const res = [];
        for (let model of data)
          for (let i = model.range_min; i <= model.range_max; i++) {
            res.push(i);
          }
        return res;
      },
    }),
    getSilencersLengths: builder.query<
      number[],
      { mfg_id: number; series_id: number }
    >({
      query: ({ mfg_id, series_id }) =>
        `/api/v1.0/master/v1.0.0/ahu/silencers/lengths/?mfg_id=${mfg_id}&series_id=${series_id}`,
      extraOptions: {
        cacheData: true,
      },
      transformResponse: (data: { length: number }[]): number[] => {
        const res = [];
        for (let model of data) res.push(model.length);
        return res;
      },
    }),
    updateSilencer: builder.mutation<ISilencer, UpdatePayload<ISilencer>>({
      query: ({ id, body, signal }) => ({
        url: `/api/v1.0/ahu/silencers/${id}`,
        method: 'PATCH',
        body,
        signal,
      }),
      transformResponse: (data: ISilencer) => {
        data.silencerParams = data.silencer_params
          ? JSON.parse(data.silencer_params)
          : {};
        return data;
      },
    }),
  }),
});

export const {
  useLazyGetSilencerQuery,
  useGetSilencerParamsQuery,
  useLazyGetSilencersCountQuery,
  useGetAllSilencersCountQuery,
  useLazyGetSilencersHeightsQuery,
  useLazyGetSilencersWidthsQuery,
  useLazyGetSilencersLengthsQuery,
  useUpdateSilencerMutation,
} = silencerApi;
